<style lang="postcss">
	.link {
		line-height: 1.25;
		-webkit-text-stroke-width: 0.1px;
		font-size: 15px;
		padding: 0.25rem 0;
		@apply truncate w-full subpixel-antialiased;
	}
	.sub-link {
		font-size: 14px;
		line-height: 1.25;
		@apply truncate w-full py-1 pl-3 relative;
	}
	.sub-link.active::before {
		content: "";
		position: absolute;
		left: 1px;
		top: 0.25rem;
		bottom: 0.25rem;
		width: 2px;
		@apply bg-theme-600 rounded-full;
	}
	.link:not(.active):hover,
	.sub-link:not(.active):hover {
		@apply text-theme-600;
	}
	.link.active,
	.sub-link.active {
		@apply text-theme-600 relative flex items-center;
	}
	:global(.help-body quote) {
		@apply block bg-gray-150 border-l-4 border-gray-500 pl-4 py-2 my-4;
	}
	:global(.help-body blockquote) {
		font-size: 0.9rem;
		background: #f4f5f6;
		border: 1px solid #ddd !important;
		border-radius: 5px;
		padding: 0 1.25rem !important;
		line-height: 1.6;
		margin: 0;
		font-style: inherit !important;
		font-weight: normal !important;
	}
	:global(.code-cover) {
		position: relative;
	}
	:global(.copy-button) {
		display: block;
		font-weight: 400;
		top: 5px;
		right: 5px;
		border-radius: 4px;
		font-size: 12px;
		border: 1.25px solid #8b96a1;
		padding: 0px 7px;
		position: absolute;
		color: #8b96a1;
		will-change: width;
		transition: width 100ms linear;
		background: rgb(34, 39, 46);
	}
	:global(.copy-button:hover) {
		border: 1.25px solid #c8cbce;
		color: #c8cbce;
	}
	.left-line {
		margin-bottom: 0.25rem;
	}
	.left-line::before {
		content: "";
		position: absolute;
		left: 1px;
		top: 0.25rem;
		bottom: 0.25rem;
		width: 2px;
		background: #e2e4e9;
	}
	:global(video) {
		max-width: 100%;
	}
	@media (max-width: 768px) {
		.link {
			font-size: 17px;
			line-height: 1.3;
		}
		.sub-link {
			font-size: 16px;
		}
	}
</style>

<script lang="ts">
	import * as icons from "$lib/Components/Icons/";
	import { page } from "$app/stores";
	import { onMount } from "svelte";
	import pkg from "flexsearch";
	const { Document } = pkg;
	import { Dialog } from "design/Components/Dialog";
	import { Highlight } from "design/Components/Highlight";
	import Navigation from "$src/lib/Components/Basic/Navigation.svelte";
	import { PUBLIC_SITE_URL } from "$env/static/public";

	export let data;
	export let query: string = "";

	let searchModal: boolean = false;
	let searchInput: HTMLInputElement;
	let filteredItems: DocPost[] = [];
	let focusedItem: number | null = 0;
	let postsWithText: { [key: string]: DocPost } = {};
	let anchorLinks: DocPost["links"] = [];
	let level: number = 1;
	let searchIndex: any;

	$: activeCategory = $page.data.category ?? $page.data.post?.category ?? null;
	$: anchorLinks = [];
	$: categories = data.categories;
	$: changeSearchModal(searchModal);

	function changeSearchModal(searchModal: boolean) {
		if (searchModal) {
			requestAnimationFrame(() => {
				searchInput.focus();
			});
		}
	}

	$: {
		if (!query) {
			filteredItems = Object.values(data.posts);
		} else {
			filteredItems = [];
			let search = searchIndex.search(query, {
				limit: 20,
				enrich: false
			});
			const searchFlat: string[] = search.reduce((acc: any, curr: any) => {
				return [...acc, ...curr.result];
			}, [] as any);
			const unique = [...new Set(searchFlat)];
			unique.forEach((result) => {
				const findPost = postsWithText[result];
				if (findPost) filteredItems.push(findPost);
			});
		}
	}

	const keydown = (event: KeyboardEvent) => {
		if (event.key === "Enter" && focusedItem !== null) {
			event.preventDefault();
			closeSearch();
			window.location.href = `/help/${filteredItems[focusedItem].path}`;
		} else if (event.key === "ArrowDown") {
			event.preventDefault();
			focusedItem = focusedItem === null ? 0 : focusedItem + 1;
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			focusedItem =
				focusedItem === null ? filteredItems.length - 1 : focusedItem - 1;
		}

		if (focusedItem == filteredItems.length) focusedItem = 0;
		if (focusedItem !== null && focusedItem < 0)
			focusedItem = filteredItems.length - 1;
	};

	const fetchPosts = async () => {
		if (!searchIndex) {
			searchIndex = new Document({
				tokenize: "reverse",
				preset: "default",
				cache: false,
				optimize: true,
				language: "en",
				resolution: 9,
				document: {
					id: "path",
					index: ["title", "text", "category", "tags", "slug", "path"],
					store: ["title", "path"]
				}
			});
		}
		let result: { [key: string]: DocPost } = {};
		const posts: DocPost[] = await fetch("/api/help/posts?text=true").then(
			(res) => res.json()
		);
		posts.forEach((post) => {
			result[post.path] = post;
		});
		return result;
	};

	const openSearch = async () => {
		searchModal = true;
		if (Object.values(postsWithText).length === 0) {
			postsWithText = await fetchPosts();
			for (let post of Object.values(postsWithText)) {
				searchIndex.add(post);
			}
		}
		document.addEventListener("keydown", keydown, false);
	};

	const closeSearch = () => {
		document.removeEventListener("keydown", keydown, false);
		searchModal = false;
	};

	$: {
		if ($page.data.post) {
			level = 3;
		} else if (activeCategory) {
			level = 2;
		} else {
			level = 1;
		}
	}

	onMount(async () => {
		const copyButtons = document.querySelectorAll(
			".copy-button"
		) as NodeListOf<HTMLButtonElement>;
		copyButtons.forEach((button) => {
			button.addEventListener("click", () => {
				const pre = button.parentNode?.querySelector("pre");
				if (!pre) return;
				navigator.clipboard.writeText(pre.textContent ?? "");
				button.textContent = "Copied";
				button.style.width = "55px";
				button.style.display = "block";
				setTimeout(() => {
					button.style.display = "";
					button.style.width = "45px";
					button.textContent = "Copy";
				}, 3000);
			});
		});
	});
</script>

<svelte:body class="help-body" />

<Navigation
	class="md:pt-10"
	label="Help"
	path="/help"
	site_base="{PUBLIC_SITE_URL}"
	items="{data.navItems}">
	<div slot="search" class="w-full">
		<button
			type="button"
			class="border-neutral-400 border-opacity-80 flex flex-row items-center relative bg-white text-base md:text-sm px-3 py-1.5 md:py-2 border rounded w-full text-left hover:border-neutral-600 hover:ring-1 ring-neutral-200"
			aria-label="Show search"
			on:click|preventDefault="{openSearch}">
			<span class="text-gray-600">Search</span>
			<span class="absolute opacity-90 right-2 size-3.5">
				{@html icons.Search}
			</span>
		</button>
	</div>
	<slot />
</Navigation>

<Dialog
	padding="{false}"
	showClose="{false}"
	align="top"
	class="bg-white lg"
	open="{searchModal}"
	on:close="{closeSearch}">
	<svelte:fragment slot="body">
		<div class="flex flex-col h-full">
			<input
				autocorrect="off"
				autocapitalize="off"
				enterkeyhint="search"
				spellcheck="false"
				placeholder="Search"
				class="px-5 py-4 text-lg {query
					? 'border-b border-gray-300'
					: ''} focus:outline-none"
				bind:this="{searchInput}"
				bind:value="{query}" />
			{#if query}
				<div
					class="flex flex-col flex-1 min-h-0 overflow-y-auto divide-y divide-gray-200">
					{#if filteredItems.length === 0}
						<div class="w-full px-5 py-4 text-left opacity-50">No results</div>
					{:else}
						{#each filteredItems as post, index}
							{@const category = categories.find(
								(cat) => cat.value === post.category
							)}
							<a
								href="/help/{post.path}"
								class:bg-gray-150="{index === focusedItem}"
								class="flex flex-col gap-1 px-5 py-3 hover:bg-gray-150">
								{#if category}
									<div
										class="flex flex-row items-center gap-0.5 text-xs opacity-50">
										<span>{category.label}</span>
										<div class="w-3">
											{@html icons.ChevronRight}
										</div>
										<span>{post.menu}</span>
									</div>
								{/if}
								<div class="text-base font-medium">
									<Highlight all="{true}" text="{post.title}" query="{query}" />
								</div>
								{#if post.text}
									<div class="text-sm line-clamp-2 opacity-70">
										<Highlight text="{post.text}" query="{query}" />
									</div>
								{/if}
							</a>
						{/each}
					{/if}
				</div>
			{/if}
		</div>
	</svelte:fragment>
</Dialog>
